import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query } from "firebase/firestore/lite";
import {
  Typography,
  Box,
  Stack,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CardActionArea,
  Backdrop,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import db from "../../firebase";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate } from "react-router-dom";

export const ActivitatsPage = () => {
  const navigate = useNavigate();

  interface activitatDB {
    id?: string;
    title?: string;
    desc?: string;
    price?: string;
    image?: string;
    docs?: Array<string>;
    contact?: {
      whats?: string;
      tel?: string;
      email?: string;
      link?: string;
    };
    address?: string;
    category?: string;
    subcategory?: string;
    top?: boolean;
    topOrder?: number;
    categoryOrder?: number;
  }

  interface CategoriesData {
    [category: string]: string[];
  }

  const [categoriesData, setCategoriesData] = useState<CategoriesData>({});
  const [activitats, setActivitats] = useState<activitatDB[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("" as string);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const getActivitats = async () => {
      try {
        const activitatsCol = query(collection(db, "activitats"));
        const activitatsSnapshot = await getDocs(activitatsCol);
        const activitatsList = activitatsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setActivitats(activitatsList);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    getActivitats();
  }, []);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "categories"));
        querySnapshot.forEach((doc) => {
          setCategoriesData(doc.data());
        });
      } catch (error) {
        console.error("Error carregant categories:", error);
      }
    };

    getCategories();
  }, []);

  // Ordenació per top/topOrder
  const sortByTop = (a: activitatDB, b: activitatDB) => {
    if (a.topOrder !== undefined && b.topOrder !== undefined) {
      return a.topOrder - b.topOrder;
    }
    if (a.topOrder !== undefined) return -1;
    if (b.topOrder !== undefined) return 1;
    return 0;
  };

  // Ordenació per categoryOrder
  const sortByCategory = (a: activitatDB, b: activitatDB) => {
    if (a.categoryOrder !== undefined && b.categoryOrder !== undefined) {
      return a.categoryOrder - b.categoryOrder;
    }
    if (a.categoryOrder !== undefined) return -1;
    if (b.categoryOrder !== undefined) return 1;
    return 0;
  };

  // Filtrar activitats
  const filteredActivities = useMemo(() => {
    let filtered = activitats;

    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((activity) => {
        const matchTitle = activity.title?.toLowerCase().includes(searchLower);
        const matchDesc = activity.desc?.toLowerCase().includes(searchLower);
        const matchCategory = activity.category
          ?.toLowerCase()
          .includes(searchLower);
        const matchSubcategory = activity.subcategory
          ?.toLowerCase()
          .includes(searchLower);
        return matchTitle || matchDesc || matchCategory || matchSubcategory;
      });
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (activity) => activity.category === selectedCategory
      );
    }

    return filtered;
  }, [activitats, searchText, selectedCategory]);

  // Activitats recomanades
  const recommendedActivities = useMemo(() => {
    return filteredActivities
      .filter((activity) => activity.top === true)
      .sort(sortByTop);
  }, [filteredActivities]);

  // Activitats per categoria
  const activitiesByCategory = useMemo(() => {
    const result: Record<string, activitatDB[]> = {};

    Object.keys(categoriesData).forEach((categoryName) => {
      const categoryActivities = filteredActivities
        .filter((activity) => activity.category === categoryName)
        .sort(sortByCategory);

      if (categoryActivities.length > 0) {
        result[categoryName] = categoryActivities.slice(0, 5);
      }
    });

    return result;
  }, [filteredActivities, categoriesData]);

  // Card d'activitat per recomanacions
  const ActivityCard = ({ activity }: { activity: activitatDB }) => (
    <Card sx={{ border: 1, borderColor: "grey.300" }}>
      <CardActionArea
        onClick={() =>
          navigate(`/activitats/${activity.id}`, {
            state: { activity },
          })
        }
      >
        <CardMedia
          component="img"
          height="180"
          image={activity.image || "https://via.placeholder.com/400x180"}
          alt={activity.title}
          sx={{ objectFit: "cover" }}
        />
        <CardContent>
          {activity.subcategory && (
            <Chip
              label={activity.subcategory}
              size="small"
              variant="outlined"
              sx={{ mb: 1, fontSize: "0.7rem" }}
            />
          )}

          <Typography variant="h6" gutterBottom>
            {activity.title}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {activity.desc}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
            <LocationIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" noWrap>
              {activity.address}
            </Typography>
          </Stack>

          <Typography variant="body2" fontWeight="600">
            {activity.price}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  // Card horitzontal per scroll
  const HorizontalActivityCard = ({ activity }: { activity: activitatDB }) => (
    <Card
      sx={{
        minWidth: 260,
        maxWidth: 260,
        border: 1,
        borderColor: "grey.300",
      }}
    >
      <CardActionArea
        onClick={() =>
          navigate(`/activitats/${activity.id}`, {
            state: { activity },
          })
        }
        sx={{ height: "100%" }}
      >
        <CardMedia
          component="img"
          height="160"
          image={activity.image || "https://via.placeholder.com/260x160"}
          alt={activity.title}
          sx={{ objectFit: "cover" }}
        />
        <CardContent sx={{ pb: 1 }}>
          {activity.subcategory && (
            <Chip
              label={activity.subcategory}
              size="small"
              variant="outlined"
              sx={{ mb: 1, fontSize: "0.7rem" }}
            />
          )}

          <Typography variant="subtitle1" gutterBottom fontWeight="600">
            {activity.title}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {activity.desc}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
            <LocationIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" noWrap>
              {activity.address}
            </Typography>
          </Stack>

          <Typography variant="body2" fontWeight="600">
            {activity.price}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  // Card "Veure totes"
  const ViewAllCard = ({
    categoryName,
    totalCount,
  }: {
    categoryName: string;
    totalCount: number;
  }) => (
    <Card
      sx={{
        minWidth: 260,
        maxWidth: 260,
        border: 1,
        borderColor: "grey.300",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.50",
      }}
    >
      <CardActionArea
        onClick={() =>
          navigate(`/category/${encodeURIComponent(categoryName)}`, {
            state: {
              categoryActivities: activitats.filter(
                (a) => a.category === categoryName
              ),
              categories: categoriesData,
              categoryName: categoryName,
            },
          })
        }
        sx={{ height: "100%", textAlign: "center", p: 3 }}
      >
        <Typography variant="h4" gutterBottom fontWeight="bold">
          +{totalCount - 5}
        </Typography>
        <Typography variant="subtitle1" gutterBottom fontWeight="600">
          Veure totes les activitats
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          de {categoryName}
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2, borderColor: "black", color: "black" }}
          endIcon={<ArrowForwardIcon />}
        >
          Veure tot
        </Button>
      </CardActionArea>
    </Card>
  );

  return (
    <Box>
      {isLoading ? (
        <Backdrop open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <Grid container spacing={2}>
          {/* Header amb cerca */}
          <Grid xs={12} mdOffset={1} md={10} sx={{ px: 2, pt: 2 }}>
            {/* Cercador */}
            <TextField
              fullWidth
              placeholder="Cerca activitats..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (searchText || selectedCategory) && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchText("");
                        setSelectedCategory("");
                      }}
                      size="small"
                    >
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  fontSize: "0.9rem",
                  py: 0.5,
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                  },
                },
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "grey.50",
                },
              }}
            />

            {/* Filtres de categoria */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Totes"
                onClick={() => setSelectedCategory("")}
                variant={selectedCategory === "" ? "filled" : "outlined"}
                sx={{
                  bgcolor: selectedCategory === "" ? "black" : "transparent",
                  color: selectedCategory === "" ? "white" : "black",
                  "&:hover": {
                    bgcolor: selectedCategory === "" ? "grey.800" : "grey.100",
                  },
                }}
              />
              {Object.keys(categoriesData).map((categoryName) => (
                <Chip
                  key={categoryName}
                  label={categoryName}
                  onClick={() =>
                    setSelectedCategory(
                      selectedCategory === categoryName ? "" : categoryName
                    )
                  }
                  variant={
                    selectedCategory === categoryName ? "filled" : "outlined"
                  }
                  sx={{
                    bgcolor:
                      selectedCategory === categoryName
                        ? "black"
                        : "transparent",
                    color:
                      selectedCategory === categoryName ? "white" : "black",
                    "&:hover": {
                      bgcolor:
                        selectedCategory === categoryName
                          ? "grey.800"
                          : "grey.100",
                    },
                  }}
                />
              ))}
            </Stack>

            {/* Indicador de resultats */}
            {(searchText || selectedCategory) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {filteredActivities.length} activitat
                {filteredActivities.length !== 1 ? "s" : ""} trobada
                {filteredActivities.length !== 1 ? "es" : ""}
              </Typography>
            )}
          </Grid>

          {/* Secció Recomanacions */}
          {recommendedActivities.length > 0 && (
            <Grid xs={12} mdOffset={1} md={10} sx={{ px: 2 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold" mb={2}>
                ⭐ Recomanacions
              </Typography>
              <Grid container spacing={2}>
                {recommendedActivities.map((activity) => (
                  <Grid xs={12} sm={6} md={4} key={activity.id}>
                    <ActivityCard activity={activity} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Seccions per categoria */}
          {Object.entries(activitiesByCategory).map(
            ([categoryName, categoryActivities]) => {
              const totalActivities = activitats.filter(
                (a) => a.category === categoryName
              ).length;

              return (
                <Grid
                  xs={12}
                  mdOffset={1}
                  md={10}
                  key={categoryName}
                  sx={{ px: 2 }}
                >
                  {/* Header de la secció */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      {categoryName}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ borderColor: "black", color: "black" }}
                      onClick={() =>
                        navigate(
                          `/category/${encodeURIComponent(categoryName)}`,
                          {
                            state: {
                              categoryActivities: activitats.filter(
                                (a) => a.category === categoryName
                              ),
                              categories: categoriesData,
                              categoryName: categoryName,
                            },
                          }
                        )
                      }
                    >
                      Tots ({totalActivities})
                    </Button>
                  </Stack>

                  {/* Scroll horitzontal */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      overflowX: "auto",
                      overflowY: "hidden",
                      pb: 2,
                      "&::-webkit-scrollbar": {
                        height: 6,
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#888",
                        "&:hover": {
                          backgroundColor: "#555",
                        },
                      },
                    }}
                  >
                    {categoryActivities.map((activity) => (
                      <HorizontalActivityCard
                        key={activity.id}
                        activity={activity}
                      />
                    ))}

                    {totalActivities > 5 && (
                      <ViewAllCard
                        categoryName={categoryName}
                        totalCount={totalActivities}
                      />
                    )}
                  </Box>
                </Grid>
              );
            }
          )}

          {/* Missatge si no hi ha resultats */}
          {filteredActivities.length === 0 && (
            <Grid xs={12} mdOffset={1} md={10} sx={{ px: 2 }}>
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No s'han trobat activitats
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prova amb una altra cerca o filtre
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};
